<template>
  <li
    class="accordion-item"
    :class="{
      available: available,
      'is-active': expanded,
      'notify-me': notifyMe,
      'rated-wine': userRating > 0.0,
      'review-wine': reviewPanelVisible,
      'show-similar': similarPanelVisible,
    }"
    role="presentation"
  >
    <VideoModal
      v-if="review.wine_video_url"
      :video-url="review.wine_video_url"
      :open="videoOpen"
      @closed="videoOpen = false"
    />
    <a
      :id="id"
      href="#"
      class="accordion-title"
      :aria-expanded="expanded"
      :aria-controls="id"
      @click.prevent="toggleExpanded"
    >
      <picture v-if="!review.rotate_for_insert && review.default_bottle_image">
        <img class="bottle-image" :src="review.default_bottle_image" alt="" />
      </picture>
      <picture v-else>
        <source :srcset="review.optimized_image_url" type="image/webp" />
        <source :srcset="review.image_url" type="image/jpeg" />
        <img class="bottle-image" :src="review.image_url" alt="" />
      </picture>
      <p class="wine-name">{{ review.name }}</p>
      <div class="stock-info">
        <p v-if="available" class="stock-info--availability">Available Now</p>
        <p v-else class="stock-info--availability">
          {{ notifyMe ? 'Saved' : 'Notify Me' }}
          <span class="icon-bell icon-bell-empty" />
        </p>
        <p v-if="review.drinking_window_summary" class="stock-info--range">
          Drink: {{ review.drinking_window_summary }}
        </p>
      </div>
      <span v-if="review.review_credited" class="credited">
        You just received a $1 credit for rating and reviewing this wine.
      </span>
      <span v-if="error" class="error">
        {{ error }}
      </span>
    </a>

    <div
      v-if="expanded"
      class="accordion-content"
      role="tabpanel"
      :aria-labelledby="id"
      :aria-hidden="!expanded"
    >
      <div class="review-notify">
        <div v-if="userRating === 0.0" class="wine-review">
          <i class="icon icon-star-outline"></i>
          <a href="#" @click.prevent="showReviewSection">Review This Wine</a>
        </div>
        <div class="wine-notify">
          <i class="icon icon-bell icon-bell-empty"></i>
          <a href="#" @click.prevent="toggleNotifications">
            {{ notifyMe ? 'Turn Notification Off' : 'Notify Me' }}
          </a>
        </div>
      </div>
      <a
        v-if="available"
        :data-product-id="review.product_id"
        href="#"
        class="button cta-button"
        @click.prevent="$emit('add-to-cart')"
      >
        Buy Again
      </a>
      <div class="wine-rating">
        <div v-if="userRating > 0.0" class="my-rating">
          <p>
            <a href="#" @click.prevent="showReviewSection">My Rating</a>
            :
            <strong>{{ userRatingDisplay }}</strong>
            <i class="icon icon-star"></i>
          </p>
        </div>
        <div v-if="averageRating > 0.0" class="average-rating">
          <p>
            Avg Rating:
            <strong>{{ averageRatingDisplay }}</strong>
            <i class="icon icon-star"></i>
          </p>
        </div>
      </div>
      <div class="wine-info">
        <div class="wine-about">
          <i class="icon icon-note"></i>
          <nuxt-link :to="review.product_url">About</nuxt-link>
        </div>
        <div v-if="review.wine_video_url" class="wine-video">
          <i class="icon icon-youtube"></i>
          <a href="#" @click.prevent="videoOpen = true">Video</a>
        </div>
      </div>
      <a
        href="#"
        class="button button-default show-similar-btn"
        @click.prevent="showSimilarProducts"
      >
        Shop Similar
        <i class="icon-chevron-down"></i>
      </a>
      <div class="show-similar__container">
        <div class="show-similar__content">
          <span class="similar-header">Shop Similar Wines</span>
          <button
            class="close-button"
            type="button"
            @click="hideSimilarSection"
          >
            <span aria-hidden="true"><i class="fi-x"></i></span>
          </button>
          <ProductGridCarousel
            :carousel-id="`${review.product_id}-recommendations`"
            :items="similarProducts"
            :title="null"
            @add-clicked="addClicked"
            @add-to-cart="suggestionAddToCart"
          />
        </div>
      </div>
    </div>
    <div class="review-panel">
      <button class="close-button" type="button" @click="hideReviewSection">
        <span aria-hidden="true"><i class="fi-x"></i></span>
      </button>
      <div class="review-panel__header">
        <p class="panel-title">My Review</p>
        <div class="wine-info">
          <picture>
            <source :srcset="review.optimized_image_url" type="image/webp" />
            <source :srcset="review.image_url" type="image/jpeg" />
            <img
              class="bottle-image"
              :src="review.image_url"
              alt=""
              height="220"
            />
          </picture>
          <p class="wine-name">{{ review.name }}</p>
        </div>
        <div class="wine-rating">
          <div class="overall-rating">
            My Rating
            <StarsInput :value="reviewRating" @change="updateRating" />
          </div>
          <div v-if="reviewRating">
            <a href="#" @click.prevent="clearRating">Clear my rating</a>
          </div>
        </div>
      </div>
      <div class="review-panel__body">
        <textarea
          v-model="reviewText"
          class="review-input"
          placeholder="Please enter your optional review here..."
        ></textarea>
      </div>
      <div class="review-panel__footer">
        <button class="button" @click="submitReview">Publish</button>
      </div>
    </div>
  </li>
</template>

<script setup>
import { isAvailable, requestedNotification } from '#common/utils/catalog'
import { getProductRecommendations } from '~/services/productServices'

const route = useRoute()
const { $auth, $config } = useNuxtApp()

// Props
const props = defineProps({
  review: {
    type: Object,
    required: true,
  },
  expandedByDefault: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
})

// Emits
const emit = defineEmits([
  'add-to-cart',
  'expanded',
  'set-notifications',
  'add-clicked',
  'suggestions-add-to-cart',
  'set-review',
  'collapsed',
])

const accordionStore = useAccordionItems()

// Data
const videoOpen = ref(false)
const reviewPanelVisible = ref(!!route.query.new_rating)
const similarPanelVisible = ref(false)
const reviewRating = ref(
  parseFloat(route.query.new_rating) ||
    parseFloat(props.review.user_rating) ||
    0,
)
const reviewText = ref(props.review.user_review || '')
const similarProducts = ref([])

const expanded = computed(() => {
  if (!accordion.id.value) {
    return props.expandedByDefault
  }
  return accordionStore.isItemExpanded(accordion.id.value, id.value)
})

const available = computed(() => {
  return isAvailable(
    $auth.user,
    props.review,
    null,
    $config.public.validSiteIds,
  )
})

const notifyMe = computed(() => {
  return requestedNotification($auth.user, props.review.product_id)
})

const id = computed(() => {
  return `${props.review.product_id}-accordion`
})

const userRating = computed(() => {
  if (!props.review || !props.review.user_rating) return 0.0
  return parseFloat(props.review.user_rating)
})

const userRatingDisplay = computed(() => {
  return Math.round(userRating.value * 10) / 10
})

const averageRating = computed(() => {
  if (!props.review || !props.review.average_rating) return 0.0
  return parseFloat(props.review.average_rating)
})

const averageRatingDisplay = computed(() => {
  return Math.round(averageRating.value * 10) / 10
})

// Inject the accordion object from the Accordion component
const accordion = inject('accordion', {
  expandedItems: ref([]),
})

// Methods
const toggleExpanded = () => {
  accordionStore.toggleItem(accordion.id.value, id.value)

  // Emit the appropriate event
  if (expanded.value) {
    emit('expanded', id.value)
  } else {
    emit('collapsed', id.value)
  }
}

const toggleNotifications = () => {
  emit('set-notifications', !notifyMe.value)
}

const showReviewSection = () => {
  reviewPanelVisible.value = true
}

const hideReviewSection = () => {
  resetPanel()
}

const showSimilarProducts = async () => {
  if (similarProducts.value.length === 0) {
    const { $axios } = useNuxtApp()
    similarProducts.value = await getProductRecommendations(
      $axios,
      $auth,
      props.review.product_id,
      3,
    )
  }
  similarPanelVisible.value = true
}

const hideSimilarSection = () => {
  similarPanelVisible.value = false
}

const submitReview = () => {
  reviewPanelVisible.value = false
  if (reviewRating.value >= 4.0) {
    showSimilarProducts()
  }
  emit('set-review', {
    user_rating: reviewRating.value,
    user_review: reviewText.value,
  })
}

const updateRating = (newRating) => {
  reviewRating.value = newRating
}

const clearRating = () => {
  updateRating(0)
}

const resetPanel = () => {
  videoOpen.value = false
  reviewPanelVisible.value = false
  similarPanelVisible.value = false
  reviewRating.value = parseFloat(props.review.user_rating) || 0
  reviewText.value = props.review.user_review || ''
}

const addClicked = () => {
  emit('add-clicked')
}

const suggestionAddToCart = (data) => {
  emit('suggestions-add-to-cart', data)
}

const collapse = () => {
  // If this item is expanded, remove it from the accordion's expandedItems
  if (expanded.value) {
    accordion.toggleItem(id.value)
  }
}

// Expose methods to parent component
defineExpose({
  collapse,
  id: computed(() => id.value),
  toggleExpanded,
})

// Lifecycle hooks
onMounted(() => {
  if (props.expandedByDefault) {
    accordionStore.openAccordionItemByDefault(accordion.id.value, id.value)
    // Emit the appropriate event
    emit('expanded', id.value)
  }
})
</script>
